import * as Sentry from "@sentry/browser";

const PUBLIC_AUTH_ERROR_MESSAGES = {
  "auth/invalid-credential":
    "No account matched that email and password. Try resetting your password or create an account.",
  "auth/user-not-found": "No account exists for that email. Create an account first.",
  "auth/wrong-password": "That password does not match this email. Try again or reset your password.",
  "auth/invalid-email": "Enter a valid email address.",
  "auth/user-disabled": "This account is not available. Contact support for help.",
  "auth/too-many-requests": "Too many attempts. Wait a few minutes, then try again or reset your password.",
  "auth/network-request-failed": "We could not reach the login service. Check your connection and try again.",
  "auth/email-already-in-use": "An account already exists for that email. Log in instead or reset your password.",
  "auth/weak-password": "Password must be at least 8 characters.",
};

const RESTRICTED_AUTH_CODES = new Set([
  "auth/operation-not-allowed",
  "auth/unauthorized-domain",
  "auth/api-key-not-valid",
  "auth/invalid-api-key",
  "auth/app-not-authorized",
  "auth/configuration-not-found",
  "auth/admin-restricted-operation",
  "auth/internal-error",
]);

const EXPECTED_USER_AUTH_CODES = new Set([
  "auth/invalid-credential",
  "auth/user-not-found",
  "auth/wrong-password",
  "auth/invalid-email",
  "auth/user-disabled",
  "auth/too-many-requests",
  "auth/email-already-in-use",
  "auth/weak-password",
  "INVALID_LOGIN_CREDENTIALS",
  "EMAIL_NOT_FOUND",
  "INVALID_PASSWORD",
  "USER_DISABLED",
  "TOO_MANY_ATTEMPTS_TRY_LATER",
]);

const SYSTEM_AUTH_CODES = new Set([
  "auth/network-request-failed",
  "missing-api-key",
  "rest-network-error",
  "rest-login-ok-sdk-session-failed",
]);

const PUBLIC_REST_AUTH_ERROR_MESSAGES = {
  INVALID_LOGIN_CREDENTIALS:
    "No account matched that email and password. Try resetting your password or create an account.",
  EMAIL_NOT_FOUND: "No account exists for that email. Create an account first.",
  INVALID_PASSWORD: "That password does not match this email. Try again or reset your password.",
  USER_DISABLED: "This account is not available. Contact support for help.",
  TOO_MANY_ATTEMPTS_TRY_LATER:
    "Too many attempts. Wait a few minutes, then try again or reset your password.",
};

const getFirebaseServerCode = (message) => {
  if (!message || typeof message !== "string") return null;

  if (message.includes("ADMIN_ONLY_OPERATION")) {
    return "ADMIN_ONLY_OPERATION";
  }

  if (message.includes("BLOCKING_FUNCTION_ERROR_RESPONSE")) {
    return "BLOCKING_FUNCTION_ERROR_RESPONSE";
  }

  return null;
};

export const getAuthErrorDetails = (error) => {
  const code =
    error?.code ||
    error?.customData?.code ||
    error?.error?.code ||
    error?.name ||
    "unknown";
  const message =
    error?.message ||
    error?.customData?.message ||
    error?.error?.message ||
    String(error);
  const serverCode = getFirebaseServerCode(message);

  return { code, message, serverCode };
};

export const getAuthErrorMessage = (error, fallbackMessage) => {
  const { code, serverCode } = getAuthErrorDetails(error);

  if (serverCode || RESTRICTED_AUTH_CODES.has(code)) {
    return fallbackMessage;
  }

  return PUBLIC_AUTH_ERROR_MESSAGES[code] || fallbackMessage;
};

export const logAuthError = (context, error, extra = {}) => {
  const details = getAuthErrorDetails(error);
  const shouldReportToSentry =
    Boolean(details.serverCode) ||
    RESTRICTED_AUTH_CODES.has(details.code) ||
    SYSTEM_AUTH_CODES.has(details.code) ||
    !EXPECTED_USER_AUTH_CODES.has(details.code);

  console.error(`${context}:`, {
    code: details.code,
    serverCode: details.serverCode,
    message: details.message,
    reportedToSentry: shouldReportToSentry,
  });

  if (!shouldReportToSentry) return;

  Sentry.withScope((scope) => {
    scope.setTag("area", "firebase-auth");
    scope.setTag("auth_context", context);
    scope.setTag("firebase_code", details.code);
    scope.setTag("auth_expected_user_error", "false");
    scope.setLevel(
      RESTRICTED_AUTH_CODES.has(details.code) || details.serverCode
        ? "fatal"
        : "error"
    );
    scope.setFingerprint(["firebase-auth", context, details.code]);

    if (details.serverCode) {
      scope.setTag("firebase_server_code", details.serverCode);
    }

    scope.setContext("firebase_auth", {
      context,
      code: details.code,
      serverCode: details.serverCode,
      ...extra,
    });

    Sentry.captureException(
      error instanceof Error ? error : new Error(details.message || context)
    );
  });
};

export const getPasswordSignInRestError = async (email, password) => {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;

  if (!apiKey) {
    return {
      ok: false,
      message: "We could not log you in right now. Please contact support.",
      code: "missing-api-key",
    };
  }

  try {
    const response = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          returnSecureToken: true,
        }),
      }
    );

    const data = await response.json();

    if (response.ok) {
      return {
        ok: true,
        message: "We could not finish logging you in. Please contact support.",
        code: "rest-login-ok-sdk-session-failed",
      };
    }

    const rawCode = data?.error?.message || `HTTP_${response.status}`;
    const code = rawCode.split(" : ")[0];

    return {
      ok: false,
      message:
        PUBLIC_REST_AUTH_ERROR_MESSAGES[code] ||
        "We could not log you in right now. Please contact support.",
      code: rawCode,
    };
  } catch (error) {
    return {
      ok: false,
      message: "We could not reach the login service. Check your connection and try again.",
      code: "rest-network-error",
    };
  }
};
